import React, { useState, useEffect, useRef } from 'react';
import './App.css';

function App() {
  const [count, setCount] = useState(0);
  const [target, setTarget] = useState(108);
  const [isListening, setIsListening] = useState(false);
  const [chantType, setChantType] = useState('ram');
  const [showSettings, setShowSettings] = useState(false);
  const [lastChant, setLastChant] = useState('');
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const recognitionRef = useRef(null);

  // PWA Install Prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    }

    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  useEffect(() => {
    // Check if browser supports Speech Recognition
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = false;
      recognition.lang = 'hi-IN'; // Hindi language for better recognition

      recognition.onresult = (event) => {
        const last = event.results.length - 1;
        const text = event.results[last][0].transcript.toLowerCase().trim();

        console.log('Heard:', text);

        // Check for Ram Ram or Radha Radha variations
        const ramPatterns = ['ram ram', 'रामराम', 'राम राम', 'ram', 'रामं'];
        const radhaPatterns = ['radha radha', 'राधाराधा', 'राधा राधा', 'radha', 'राधे'];

        let isValidChant = false;

        if (chantType === 'ram' && ramPatterns.some(pattern => text.includes(pattern))) {
          isValidChant = true;
        } else if (chantType === 'radha' && radhaPatterns.some(pattern => text.includes(pattern))) {
          isValidChant = true;
        }

        if (isValidChant && count < target) {
          setCount(prev => prev + 1);
          setLastChant(text);
        }
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
      };

      recognitionRef.current = recognition;
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [chantType, count, target]);

  const toggleListening = () => {
    if (!recognitionRef.current) {
      alert('Speech recognition is not supported in your browser. Please use Chrome or Edge.');
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  const resetCounter = () => {
    setCount(0);
    setLastChant('');
  };

  const progress = (count / target) * 100;

  return (
    <div className={`App ${chantType}-theme`}>
      <div className="background-animation">
        <div className="lotus lotus-1"></div>
        <div className="lotus lotus-2"></div>
        <div className="lotus lotus-3"></div>
      </div>

      <div className="container">
        {showInstallPrompt && (
          <div className="install-prompt">
            <div className="install-content">
              <span>📱 Install app for offline use</span>
              <div className="install-buttons">
                <button className="install-btn" onClick={handleInstallClick}>
                  Install
                </button>
                <button className="dismiss-btn" onClick={() => setShowInstallPrompt(false)}>
                  ✕
                </button>
              </div>
            </div>
          </div>
        )}

        <header className="app-header">
          <h1 className="app-title">
            {chantType === 'ram' ? '🕉️ राम नाम जप 🕉️' : '🌸 राधा नाम जप 🌸'}
          </h1>
          <button className="settings-btn" onClick={() => setShowSettings(!showSettings)}>
            ⚙️
          </button>
        </header>

        {showSettings && (
          <div className="settings-panel">
            <div className="setting-group">
              <label>Chant Type:</label>
              <div className="radio-group">
                <button
                  className={`option-btn ${chantType === 'ram' ? 'active' : ''}`}
                  onClick={() => setChantType('ram')}
                >
                  राम राम
                </button>
                <button
                  className={`option-btn ${chantType === 'radha' ? 'active' : ''}`}
                  onClick={() => setChantType('radha')}
                >
                  राधा राधा
                </button>
              </div>
            </div>
            <div className="setting-group">
              <label>Target Count:</label>
              <div className="radio-group">
                <button
                  className={`option-btn ${target === 108 ? 'active' : ''}`}
                  onClick={() => setTarget(108)}
                >
                  108
                </button>
                <button
                  className={`option-btn ${target === 1008 ? 'active' : ''}`}
                  onClick={() => setTarget(1008)}
                >
                  1008
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="main-counter">
          <div className="count-display">
            <div className="count-number">{count}</div>
            <div className="count-label">/ {target}</div>
          </div>

          <div className="progress-ring">
            <svg width="300" height="300">
              <circle
                className="progress-ring-circle-bg"
                stroke="#ffffff20"
                strokeWidth="15"
                fill="transparent"
                r="135"
                cx="150"
                cy="150"
              />
              <circle
                className="progress-ring-circle"
                stroke={chantType === 'ram' ? '#ff9933' : '#ff69b4'}
                strokeWidth="15"
                fill="transparent"
                r="135"
                cx="150"
                cy="150"
                strokeDasharray={`${2 * Math.PI * 135}`}
                strokeDashoffset={`${2 * Math.PI * 135 * (1 - progress / 100)}`}
              />
            </svg>
          </div>

          {count >= target && (
            <div className="completion-message">
              <div className="celebration">🎉 जय हो! 🎉</div>
              <div className="completion-text">You completed {target} chants!</div>
            </div>
          )}
        </div>

        {lastChant && (
          <div className="last-chant">
            Last heard: "{lastChant}"
          </div>
        )}

        <div className="controls">
          <button
            className={`mic-btn ${isListening ? 'listening' : ''}`}
            onClick={toggleListening}
          >
            {isListening ? '🎤 Listening...' : '🎤 Start Chanting'}
          </button>

          <button className="reset-btn" onClick={resetCounter}>
            🔄 Reset
          </button>
        </div>

        <div className="instructions">
          <p>
            {isListening
              ? `Chant "${chantType === 'ram' ? 'Ram Ram' : 'Radha Radha'}" clearly`
              : 'Click "Start Chanting" and begin your spiritual practice'}
          </p>
        </div>
      </div>
    </div>
  );
}

export default App;
